import React, { useCallback, useContext, useMemo } from 'react'
import {
    ApiShopperLandingPageLink,
    VLPVariant,
    VLPOption,
    VariantSelectorProps,
    SelectedOption,
    OptionKeyMap,
    isOptionAvailable,
    getVariantImageForOption,
    isColorOption,
    hasSwatchData,
    orderOptionsSwatchFirst,
    buildOptionKeyMap,
    findVariantByOptions,
    findFirstAvailableVariantWithOption,
    hasAnyAvailableVariantForOption,
    getOptionsFromVariant,
} from '@storefronts/common-types'

import { AnalyticsContext } from '../../../context/analyticsContext'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import { useVariantSelection } from '../../../hooks/useVariantSelection'
import { getVariantPrices } from '../../../utils/priceFormat'
import {
    filterOneTimePurchaseOptionValues,
    getRequiresSellingPlan,
    variantHasOneTimePurchaseOption,
} from '../../../utils/productDisplay'
import { VariantTitle } from './VariantTitle'
import {
    TabsList,
    UnavailableText,
    VariantContainer,
    VariantGroup as StyledVariantGroup,
    VariantOptionsContainer,
} from '../../styled/variantComponents'
import { ColorVariantOption } from './ColorVariantOption'
import { SwatchVariantOption } from './SwatchVariantOption'
import { TextVariantOption } from './TextVariantOption'

const MAX_VISIBLE_OPTIONS = 3

interface VariantOptionItemProps {
    option: VLPOption
    value: string
    valueIndex: number
    isActive: boolean
    isAvailable: boolean
    isClickable: boolean
    variants: VLPVariant[]
    selectedOptions: SelectedOption[]
    optionKeyMap: OptionKeyMap
    handleOptionSelect: (name: string, value: string) => void
    showVariantImagesAsColors?: boolean
    lpLink: ApiShopperLandingPageLink
}

const VariantOptionItem: React.FC<VariantOptionItemProps> = ({
    option,
    value,
    valueIndex,
    isActive,
    isAvailable,
    isClickable,
    variants,
    selectedOptions,
    optionKeyMap,
    handleOptionSelect,
    showVariantImagesAsColors,
    lpLink,
}) => {
    const { landingPage } = useContext(ViddyLPContext)

    const onClick = isClickable
        ? () => handleOptionSelect(option.name, value)
        : undefined

    if (hasSwatchData(option)) {
        return (
            <SwatchVariantOption
                option={option}
                value={value}
                valueIndex={valueIndex}
                isSelected={isActive}
                isAvailable={isAvailable}
                isClickable={isClickable}
                onClick={onClick}
            />
        )
    }

    if (isColorOption(option) && showVariantImagesAsColors) {
        return (
            <ColorVariantOption
                option={option}
                value={value}
                isSelected={isActive}
                isAvailable={isAvailable}
                isClickable={isClickable}
                showVariantImages={true}
                getVariantImage={() =>
                    getVariantImageForOption({
                        variants,
                        optionName: option.name,
                        optionValue: value,
                        optionKeyMap,
                    })
                }
                getVariantPrice={() => {
                    const optionsWithCurrentSelection = selectedOptions.map(
                        (opt) => {
                            if (opt.name === option.name) {
                                return { ...opt, value }
                            }
                            return opt
                        },
                    )

                    const variant = findVariantByOptions(
                        variants,
                        optionsWithCurrentSelection,
                        optionKeyMap,
                    )
                    if (!variant) return null

                    const { price, compareAtPrice } = getVariantPrices(
                        variant,
                        landingPage?.discount || null,
                        lpLink,
                    )

                    return {
                        price,
                        compareAtPrice,
                        hasDiscount:
                            compareAtPrice !== null && price < compareAtPrice,
                    }
                }}
                onClick={onClick}
            />
        )
    }

    return (
        <TextVariantOption
            value={value}
            isSelected={isActive}
            isAvailable={isAvailable}
            isClickable={isClickable}
            onClick={onClick}
        />
    )
}

interface VariantOptionGroupProps {
    option: VLPOption
    variants: VLPVariant[]
    selectedOptions: SelectedOption[]
    optionKeyMap: OptionKeyMap
    visibleOptionNames: Set<string>
    handleOptionSelect: (name: string, value: string) => void
    showVariantImagesAsColors?: boolean
    lpLink: ApiShopperLandingPageLink
}

const VariantOptionGroup: React.FC<VariantOptionGroupProps> = ({
    option,
    variants,
    selectedOptions,
    optionKeyMap,
    visibleOptionNames,
    handleOptionSelect,
    showVariantImagesAsColors,
    lpLink,
}) => {
    if (option.values.length === 1 || !visibleOptionNames.has(option.name))
        return null

    const isSwatch = hasSwatchData(option)

    const selectedOption = selectedOptions.find(
        (opt) => opt.name === option.name,
    )

    return (
        <StyledVariantGroup role="radiogroup" aria-label={option.name}>
            <VariantTitle selectedOptions={selectedOptions} option={option} />
            <TabsList>
                {option.values.map(({ value }, index) => {
                    const isClickable = hasAnyAvailableVariantForOption({
                        variants,
                        optionName: option.name,
                        optionValue: value,
                        optionKeyMap,
                    })

                    const isAvailable = isSwatch
                        ? isClickable
                        : isClickable &&
                          isOptionAvailable({
                              variants,
                              optionName: option.name,
                              optionValue: value,
                              selectedOptions,
                              optionKeyMap,
                          })

                    return (
                        <VariantOptionItem
                            key={index}
                            option={option}
                            value={value}
                            valueIndex={index}
                            isActive={selectedOption?.value === value}
                            isAvailable={isAvailable}
                            isClickable={isClickable}
                            variants={variants}
                            selectedOptions={selectedOptions}
                            optionKeyMap={optionKeyMap}
                            handleOptionSelect={handleOptionSelect}
                            showVariantImagesAsColors={
                                showVariantImagesAsColors
                            }
                            lpLink={lpLink}
                        />
                    )
                })}
            </TabsList>
        </StyledVariantGroup>
    )
}

export const VariantSelector: React.FC<VariantSelectorProps> = ({
    link,
    onVariantSelect,
    initialSelectedVariantShopifyId = null,
}) => {
    const { Analytics } = useContext(AnalyticsContext)
    const { landingPage } = useContext(ViddyLPContext)

    const handleVariantSelect = useCallback(
        (variant: VLPVariant) => onVariantSelect(variant, true),
        [onVariantSelect],
    )

    // Subscription-only products: hide Shopify "One-Time" order-type values so
    // shoppers can't pick a one-time variant alongside the subscribe card.
    // Virtual-product links can mix underlying products with different
    // subscription requirements, so this is evaluated per variant rather
    // than once for the whole link.
    const linkForSelection = useMemo(() => {
        const variants = link.variantInfo ?? []
        const filteredVariants = variants.filter(
            (variant) =>
                !variantHasOneTimePurchaseOption(variant) ||
                !getRequiresSellingPlan(link, variant.shopifyProductId),
        )
        if (filteredVariants.length === variants.length) return link
        return {
            ...link,
            optionInfo: filterOneTimePurchaseOptionValues(
                link.optionInfo,
                filteredVariants,
            ),
            variantInfo: filteredVariants,
        }
    }, [link])

    const { selectedOptions, setSelectedOptions } = useVariantSelection(
        linkForSelection as Required<ApiShopperLandingPageLink>,
        initialSelectedVariantShopifyId,
    )

    const options = useMemo(
        () => linkForSelection.optionInfo || [],
        [linkForSelection.optionInfo],
    )
    const variants = useMemo(
        () => linkForSelection.variantInfo || [],
        [linkForSelection.variantInfo],
    )

    const optionKeyMap = useMemo(() => buildOptionKeyMap(options), [options])

    const displayOptions = useMemo(
        () => orderOptionsSwatchFirst(options),
        [options],
    )

    const visibleOptionNames = useMemo(
        () =>
            new Set(
                displayOptions.slice(0, MAX_VISIBLE_OPTIONS).map((o) => o.name),
            ),
        [displayOptions],
    )

    // Only show variant images when the link setting is on AND every variant has an image
    const showVariantImagesAsColors = useMemo(() => {
        if (!link.showVariantImagesAsColors || variants.length === 0)
            return false
        return variants.every(
            (v) => v.featuredImage != null && v.featuredImage !== '',
        )
    }, [link.showVariantImagesAsColors, variants])

    const handleOptionSelect = useCallback(
        (name: string, value: string) => {
            const available = isOptionAvailable({
                variants,
                optionName: name,
                optionValue: value,
                selectedOptions,
                optionKeyMap,
            })
            Analytics.TRACK_VARIANT_SELECTION(
                link.title,
                name,
                value,
                available,
            )

            if (available) {
                const newSelection = selectedOptions.map((opt) =>
                    opt.name === name ? { ...opt, value } : opt,
                )
                const matchingVariant = findVariantByOptions(
                    variants,
                    newSelection,
                    optionKeyMap,
                )
                if (matchingVariant) {
                    handleVariantSelect(matchingVariant)
                }
                setSelectedOptions(newSelection)
            } else {
                const firstAvailableVariant =
                    findFirstAvailableVariantWithOption({
                        variants,
                        optionName: name,
                        optionValue: value,
                        optionKeyMap,
                    })

                if (firstAvailableVariant) {
                    const newSelection = getOptionsFromVariant(
                        firstAvailableVariant,
                        options,
                        optionKeyMap,
                    )
                    setSelectedOptions(newSelection)
                    handleVariantSelect(firstAvailableVariant)
                }
            }
        },
        [
            variants,
            selectedOptions,
            options,
            optionKeyMap,
            Analytics,
            link.title,
            handleVariantSelect,
            setSelectedOptions,
        ],
    )

    if (!options.length || !variants.length) return null

    // VinylMePlease - hide variant selector
    // If more than three shops are added, we should make this configurable via admin
    // Ensure you add the shopify shop name to @Cart.tsx to hide the variant title
    if (
        landingPage?.client?.shopifyShop === 'vmp-app.myshopify.com' ||
        landingPage?.client?.shopifyShop === 'mojo-development.myshopify.com'
    ) {
        return null
    }

    const hasSelection = selectedOptions.some((opt) => opt.value)
    const hasUnavailableOptions =
        hasSelection &&
        displayOptions
            .filter(
                (option) =>
                    option.values.length > 1 &&
                    visibleOptionNames.has(option.name),
            )
            .some((option) => {
                const isSwatch = hasSwatchData(option)
                return option.values.some(({ value }) =>
                    isSwatch
                        ? !hasAnyAvailableVariantForOption({
                              variants,
                              optionName: option.name,
                              optionValue: value,
                              optionKeyMap,
                          })
                        : !isOptionAvailable({
                              variants,
                              optionName: option.name,
                              optionValue: value,
                              selectedOptions,
                              optionKeyMap,
                          }),
                )
            })

    return (
        <VariantContainer role="group" aria-label="Product variants">
            <VariantOptionsContainer>
                {displayOptions.map((option) => (
                    <VariantOptionGroup
                        key={option.name}
                        option={option}
                        variants={variants}
                        selectedOptions={selectedOptions}
                        optionKeyMap={optionKeyMap}
                        visibleOptionNames={visibleOptionNames}
                        handleOptionSelect={handleOptionSelect}
                        showVariantImagesAsColors={showVariantImagesAsColors}
                        lpLink={link}
                    />
                ))}
                {hasUnavailableOptions && (
                    <UnavailableText>
                        Some options are unavailable with your current selection
                    </UnavailableText>
                )}
            </VariantOptionsContainer>
        </VariantContainer>
    )
}
